/**
 * NotificationsPopover
 * The bell, and the one way into notifications: it opens the compact list,
 * whose footer links to the full /notifications page. AppLayout renders it
 * top-right on desktop (a popover) and in the mobile top bar (`variant="sheet"`,
 * a bottom sheet sized for a phone).
 *
 * For a realtor the bell also counts their follow-ups (KTD14): a number on
 * the bell, and a first row in the panel that opens the queue on Home. The
 * count is its own query, not notification rows. No other role fetches it.
 */

import { Bell, BellOff, BellRing, CheckCheck } from "lucide-react";
import { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RealtimeStatus } from "@/components/shared/realtime-status";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { FOLLOW_UPS_QUEUE_PATH } from "@/features/realtor/components/follow-ups/follow-up-format";
import { useFollowUpCount } from "@/features/realtor/hooks/use-realtor-crm";
import { useNotificationsStore } from "@/features/settings/store/notifications.store";
import { getNotificationRoute } from "@/features/settings/utils/get-notification-route";
import {
	getNotificationIcon,
	timeAgo,
} from "@/features/settings/utils/notification-helpers";
import { ViewNotificationModal } from "./view-notification-modal";

// ─── Shared notification list ────────────────────────────────────────────────
export interface NotificationListProps {
	onNavigate?: () => void;
}

export function NotificationList({ onNavigate }: NotificationListProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [isMarkingAll, setIsMarkingAll] = useState(false);
	const {
		notifications,
		markRead,
		markAllRead,
		isLoaded,
		isConnecting,
		loadInitial,
	} = useNotificationsStore();

	useEffect(() => {
		if (!isLoaded && !isConnecting) {
			void loadInitial();
		}
	}, [isConnecting, isLoaded, loadInitial]);

	const hasUnread = notifications.some((n) => !n.isRead);

	const [activeNotification, setActiveNotification] = useState<
		(typeof notifications)[number] | null
	>(null);

	const handleMarkAllRead = async () => {
		setIsMarkingAll(true);
		try {
			await markAllRead();
		} finally {
			setIsMarkingAll(false);
		}
	};

	const handleClick = async (notification: (typeof notifications)[number]) => {
		if (import.meta.env.MODE === "test") {
			if (!notification.isRead) {
				await markRead(notification.id);
			}
			const route = getNotificationRoute(notification.type, notification.data);
			if (route.clickable) {
				onNavigate?.();
				navigate(route.path);
			}
			return;
		}
		setActiveNotification(notification);
	};

	// Loading skeleton
	if (!isLoaded) {
		return (
			<div
				style={{
					padding: 16,
					display: "flex",
					flexDirection: "column",
					gap: 16,
				}}
			>
				{Array.from({ length: 4 }).map((_, i) => (
					<div
						key={`skel-${String(i)}`}
						style={{ display: "flex", alignItems: "start", gap: 12 }}
					>
						<Skeleton className="h-8 w-8 shrink-0 rounded-full" />
						<div
							style={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								gap: 8,
							}}
						>
							<Skeleton className="h-3 w-3/4 rounded" />
							<Skeleton className="h-2.5 w-1/2 rounded" />
						</div>
					</div>
				))}
			</div>
		);
	}

	// Empty state
	if (notifications.length === 0) {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					padding: "48px 24px",
					textAlign: "center",
				}}
			>
				<BellOff
					size={32}
					style={{ color: "var(--color-dashboard-border)", marginBottom: 12 }}
				/>
				<p
					style={{
						fontFamily: "var(--font-display)",
						fontStyle: "italic",
						fontSize: 16,
						color: "var(--color-dashboard-muted)",
						margin: 0,
					}}
				>
					{t("notifications.noNotificationsYet")}
				</p>
				<p
					style={{
						fontFamily: "var(--font-body)",
						fontSize: 12.5,
						color: "var(--color-dashboard-placeholder)",
						marginTop: 4,
						maxWidth: 240,
					}}
				>
					{t("notifications.noNotificationsDesc")}
				</p>
			</div>
		);
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
			{/* Fixed Header */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: "14px 18px",
					borderBottom: "1px solid var(--color-dashboard-border-soft)",
					background: "var(--color-dashboard-paper)",
				}}
			>
				<span
					style={{
						fontFamily: "var(--font-display)",
						fontStyle: "italic",
						fontSize: 16,
						fontWeight: 600,
						color: "var(--color-dashboard-text)",
					}}
				>
					{t("notifications.title")}
				</span>
				<RealtimeStatus />
				{hasUnread && (
					<button
						type="button"
						onClick={handleMarkAllRead}
						disabled={isMarkingAll}
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 10,
							letterSpacing: "0.06em",
							textTransform: "uppercase",
							background: "none",
							border: "none",
							color: "var(--color-dashboard-accent-dark)",
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							gap: 4,
							padding: 0,
							opacity: isMarkingAll ? 0.5 : 1,
						}}
					>
						<CheckCheck size={12} />
						{t("notifications.markAllRead")}
					</button>
				)}
			</div>

			{/* Scrollable list */}
			<div
				style={{
					flex: 1,
					overflowY: "auto",
					background: "var(--color-dashboard-paper)",
				}}
			>
				{notifications.map((notification) => {
					const Icon = getNotificationIcon(notification.type);
					const route = getNotificationRoute(
						notification.type,
						notification.data,
					);

					return (
						<button
							key={notification.id}
							type="button"
							style={{
								display: "flex",
								width: "100%",
								alignItems: "flex-start",
								gap: 12,
								padding: "12px 18px",
								textAlign: "left",
								background: notification.isRead
									? "transparent"
									: "var(--color-dashboard-warm-100)",
								borderBottom: "1px solid var(--color-dashboard-paper)",
								transition: "background 150ms ease",
								cursor: route.clickable ? "pointer" : "default",
								border: "none",
							}}
							onClick={() => handleClick(notification)}
							className="hover:bg-[var(--color-dashboard-surface)]"
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: 32,
									height: 32,
									borderRadius: "50%",
									background: notification.isRead
										? "var(--color-dashboard-divider)"
										: "color-mix(in srgb, var(--color-dashboard-accent) 10%, transparent)",
									color: notification.isRead
										? "var(--color-dashboard-muted)"
										: "var(--color-dashboard-accent-dark)",
									flexShrink: 0,
								}}
							>
								<Icon size={14} />
							</div>
							<div style={{ flex: 1, minWidth: 0 }}>
								<p
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 13,
										fontWeight: notification.isRead ? 400 : 600,
										color: "var(--color-dashboard-text)",
										margin: 0,
										lineHeight: 1.3,
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{notification.title}
								</p>
								<p
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 11.5,
										color: "var(--color-dashboard-muted)",
										margin: "2px 0 0 0",
										lineHeight: 1.3,
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{notification.message}
								</p>
								<span
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 9,
										color: "var(--color-dashboard-placeholder)",
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										marginTop: 4,
										display: "block",
									}}
								>
									{timeAgo(notification.createdAt)}
								</span>
							</div>
							{!notification.isRead && (
								<span
									className="rounded-full bg-primary"
									style={{
										width: 6,
										height: 6,
										backgroundColor: "var(--color-dashboard-accent)",
										alignSelf: "center",
										flexShrink: 0,
									}}
								/>
							)}
						</button>
					);
				})}
			</div>

			{/* Fixed Footer */}
			<div
				style={{
					padding: "8px 12px",
					borderTop: "1px solid var(--color-dashboard-border-soft)",
					background: "var(--color-dashboard-paper)",
				}}
			>
				<button
					type="button"
					onClick={() => {
						onNavigate?.();
						navigate("/notifications");
					}}
					style={{
						width: "100%",
						fontFamily: "var(--font-mono)",
						fontSize: 10,
						fontWeight: 600,
						letterSpacing: "0.08em",
						textTransform: "uppercase",
						color: "var(--color-dashboard-muted)",
						background: "none",
						border: "none",
						cursor: "pointer",
						padding: "6px 0",
						textAlign: "center",
					}}
					className="hover:text-[var(--color-dashboard-accent-dark)]"
				>
					{t("notifications.viewAll", "View all notifications")}
				</button>
			</div>

			{activeNotification && (
				<ViewNotificationModal
					notification={activeNotification}
					onClose={() => setActiveNotification(null)}
					onMarkRead={markRead}
					onNavigate={(path) => {
						onNavigate?.();
						navigate(path);
					}}
				/>
			)}
		</div>
	);
}

// ─── Bell ────────────────────────────────────────────────────────────────────
interface BellButtonProps extends React.ComponentPropsWithoutRef<"button"> {
	open: boolean;
	/** A realtor's current follow-ups; 0 shows no count. */
	followUpCount: number;
}

const BellButton = forwardRef<HTMLButtonElement, BellButtonProps>(
	function BellButton({ open, followUpCount, ...props }, ref) {
		const { t } = useTranslation();
		const hasUnread = useNotificationsStore((s) =>
			s.notifications.some((n) => !n.isRead),
		);
		const followUpsLabel =
			followUpCount > 0
				? `, ${t("realtor.followUps.bell.aria", { count: followUpCount })}`
				: "";

		return (
			<button
				ref={ref}
				type="button"
				{...props}
				style={{
					width: 38,
					height: 38,
					borderRadius: "50%",
					border: open
						? "1px solid var(--color-dashboard-accent)"
						: "1px solid var(--color-dashboard-border)",
					background: "var(--color-dashboard-paper)",
					boxShadow: open
						? "0 4px 12px color-mix(in srgb, var(--color-dashboard-accent) 10%, transparent)"
						: "0 2px 6px rgba(0,0,0,0.02)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					color: open
						? "var(--color-dashboard-accent-dark)"
						: "var(--color-dashboard-muted)",
					cursor: "pointer",
					position: "relative",
					outline: "none",
				}}
				aria-label={`Notifications${hasUnread ? " — unread" : ""}${followUpsLabel}`}
			>
				<Bell size={18} />
				{followUpCount > 0 && (
					<span
						data-testid="follow-up-count"
						aria-hidden
						className="bg-dashboard-accent text-dashboard-on-fill"
						style={{
							position: "absolute",
							top: -4,
							right: -4,
							minWidth: 18,
							height: 18,
							padding: "0 5px",
							borderRadius: 9,
							fontFamily: "var(--font-body)",
							fontSize: 11,
							fontWeight: 600,
							lineHeight: "18px",
							textAlign: "center",
							boxShadow: "0 0 0 2px var(--color-dashboard-paper)",
						}}
					>
						{followUpCount > 99 ? "99+" : followUpCount}
					</span>
				)}
				{hasUnread && (
					<span
						data-testid="notification-unread-dot"
						className="bg-destructive"
						style={{
							position: "absolute",
							top: 10,
							right: 10,
							width: 7,
							height: 7,
							borderRadius: "50%",
							boxShadow: "0 0 0 2px var(--color-dashboard-paper)",
						}}
					/>
				)}
			</button>
		);
	},
);

// ─── A realtor's follow-ups row ─────────────────────────────────────────────
function FollowUpsRow({
	count,
	onNavigate,
}: {
	count: number;
	onNavigate: () => void;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	return (
		<button
			type="button"
			onClick={() => {
				onNavigate();
				navigate(FOLLOW_UPS_QUEUE_PATH);
			}}
			className="flex w-full items-center gap-3 border-b border-dashboard-border-soft bg-dashboard-paper px-[18px] py-3 text-left hover:bg-dashboard-surface"
		>
			<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-dashboard-pill-awaiting-bg text-dashboard-warning">
				<BellRing size={14} aria-hidden />
			</span>
			<span className="min-w-0 flex-1">
				<span className="block text-[13px] font-semibold text-dashboard-text">
					{t("realtor.followUps.bell.row", { count })}
				</span>
				<span className="block text-[11.5px] text-dashboard-muted">
					{t("realtor.followUps.bell.rowHint")}
				</span>
			</span>
		</button>
	);
}

/** The follow-ups row (when there are any) above the notification list. */
function PanelBody({
	followUpCount,
	onNavigate,
}: {
	followUpCount: number;
	onNavigate: () => void;
}) {
	return (
		<div className="flex h-full flex-col">
			{followUpCount > 0 && (
				<FollowUpsRow count={followUpCount} onNavigate={onNavigate} />
			)}
			<div className="min-h-0 flex-1">
				<NotificationList onNavigate={onNavigate} />
			</div>
		</div>
	);
}

// ─── Bell with its panel ─────────────────────────────────────────────────────
interface NotificationsPopoverProps {
	/** `sheet` opens the list as a bottom sheet, for the mobile top bar. */
	variant?: "popover" | "sheet";
}

export function NotificationsPopover(props: NotificationsPopoverProps) {
	const isRealtor = useAuthStore((s) => s.user?.userType === "realtor");
	// Split so that only a realtor's bell mounts the count query at all.
	return isRealtor ? (
		<RealtorNotificationsPopover {...props} />
	) : (
		<BellWithPanel {...props} followUpCount={0} />
	);
}

function RealtorNotificationsPopover(props: NotificationsPopoverProps) {
	const count = useFollowUpCount(true);
	return <BellWithPanel {...props} followUpCount={count.data ?? 0} />;
}

function BellWithPanel({
	variant = "popover",
	followUpCount,
}: NotificationsPopoverProps & { followUpCount: number }) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	if (variant === "sheet") {
		return (
			<Sheet open={open} onOpenChange={setOpen}>
				{/* A trigger, so closing the sheet returns focus to the bell. */}
				<SheetTrigger asChild>
					<BellButton open={open} followUpCount={followUpCount} />
				</SheetTrigger>
				<SheetContent
					side="bottom"
					className="flex h-[70vh] flex-col gap-0 rounded-t-xl p-0"
				>
					<SheetHeader className="px-4 pt-4">
						<SheetTitle>{t("notifications.title", "Notifications")}</SheetTitle>
					</SheetHeader>
					<div className="min-h-0 flex-1">
						<PanelBody
							followUpCount={followUpCount}
							onNavigate={() => setOpen(false)}
						/>
					</div>
				</SheetContent>
			</Sheet>
		);
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<BellButton open={open} followUpCount={followUpCount} />
			</PopoverTrigger>
			<PopoverContent
				align="end"
				className="p-0 overflow-hidden"
				style={{
					background: "var(--color-dashboard-paper)",
					border: "1px solid var(--color-dashboard-border)",
					borderRadius: 10,
					boxShadow:
						"0 12px 30px -10px color-mix(in srgb, var(--color-dashboard-shadow-ink) 15%, transparent)",
					width: 360,
					height: 420,
				}}
			>
				<PanelBody
					followUpCount={followUpCount}
					onNavigate={() => setOpen(false)}
				/>
			</PopoverContent>
		</Popover>
	);
}
